/**
 * One row of the Forecast grid — Open PO, Forecast or Total for a part block.
 *
 * Every row carries the full identifying set (customer, supplier, part…) so
 * rows for different parts stay distinguishable when sorted together. Cells
 * with quantity drill down to their records; Total rows are calculated and
 * never drill (CR-5310: Future/Total drill on Forecast rows only).
 */
import StockStatusIndicator from '../../StockStatusIndicator';
import {
  ForecastGridLayout,
  ForecastTableRowWithStock,
  RECORD_TYPE_COLORS,
  RECORD_TYPE_LABELS,
  rowField,
} from './forecastGridTypes';

interface ForecastGridRowProps {
  row: ForecastTableRowWithStock;
  layout: ForecastGridLayout;
  /** Zero-suppressed quantity formatter ('-' for 0 / blank). */
  formatQty: (value: string | undefined) => string;
  onCellClick: (row: ForecastTableRowWithStock, bucketKey: string, bucketLabel: string) => void;
}

const INHERIT = { backgroundColor: 'inherit' };

function isNonZero(value: string | undefined): boolean {
  return !!value && parseFloat(value) !== 0;
}

export function ForecastGridRow({ row, layout, formatQty, onCellClick }: ForecastGridRowProps) {
  const {
    showPoNumber, showSupplierPartNo, showStockStatus,
    showFirmDays, weeksOnly, weekColumns, monthColumns,
  } = layout;
  const isTotal = row.record_type === 3;
  const poNumbers = row.po_numbers ?? [];
  const hasArrears = !!row.arrears && parseFloat(row.arrears) > 0;
  const arrearsClickable = !isTotal && hasArrears;

  const bucketCell = (key: string, label: string, tint: string, hover: string) => {
    const val = rowField(row, key);
    const clickable = !isTotal && isNonZero(val);
    return (
      <td
        key={key}
        className={`px-2 py-2 text-right whitespace-nowrap font-mono text-xs ${tint} ${clickable ? `cursor-pointer ${hover}` : ''}`}
        onClick={clickable ? () => onCellClick(row, key, label) : undefined}
      >
        {clickable ? (
          <span className="text-primary underline decoration-dotted">{formatQty(val)}</span>
        ) : formatQty(val)}
      </td>
    );
  };

  // Future and Total — drillable for Forecast rows (record_type 1) only.
  const summaryCell = (key: 'future' | 'total', label: string, extra: string) => {
    const val = row[key];
    const clickable = row.record_type === 1 && isNonZero(val);
    return (
      <td
        className={`px-3 py-2 text-right whitespace-nowrap font-mono text-xs bg-slate-100 ${extra} ${clickable ? 'cursor-pointer hover:bg-slate-200' : ''}`}
        onClick={clickable ? () => onCellClick(row, key, label) : undefined}
      >
        {clickable ? (
          <span className="text-primary underline decoration-dotted">{formatQty(val)}</span>
        ) : formatQty(val)}
      </td>
    );
  };

  return (
    <tr className={`${RECORD_TYPE_COLORS[row.record_type]} hover:opacity-80 transition-opacity`}>
      <td className="px-3 py-2 text-sm sticky left-0 z-10 min-w-[180px] max-w-[220px] break-words" style={INHERIT}>
        {rowField(row, 'customer') || '-'}
      </td>
      <td className="px-3 py-2 text-sm min-w-[160px] max-w-[200px] break-words" style={INHERIT}>
        {rowField(row, 'supplier') || 'Unassigned'}
      </td>
      <td className="px-3 py-2 whitespace-nowrap font-medium" style={INHERIT}>
        {row.part_number}
      </td>
      {/* Open PO(s) behind this part — all of them, comma separated
          (customer request 2026-08-13). Empty on Forecast rows of PO-line
          customers: that demand is not on a PO yet (review item 35a). */}
      {showPoNumber && (
        <td className="px-3 py-2 text-sm font-mono max-w-[220px] break-words" style={INHERIT} title={poNumbers.join(', ')}>
          {poNumbers.length === 0 ? '-' : poNumbers.join(', ')}
        </td>
      )}
      {showSupplierPartNo && (
        <td className="px-3 py-2 text-sm whitespace-nowrap font-mono" style={INHERIT}>
          {row.supplier_part_number || '-'}
        </td>
      )}
      <td className="px-3 py-2 text-sm max-w-[220px]" style={INHERIT}>
        {row.description || '-'}
      </td>
      <td className="px-3 py-2 text-sm whitespace-nowrap" style={INHERIT}>
        {rowField(row, 'uom') || '-'}
      </td>
      {showStockStatus && (
        <td className="px-2 py-2 text-center whitespace-nowrap">
          {row.stock_status && <StockStatusIndicator stockStatus={row.stock_status} />}
        </td>
      )}
      <td className="px-3 py-2 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
          row.record_type === 1 ? 'bg-orange-50 text-orange-700 border border-orange-200' :
          row.record_type === 2 ? 'bg-green-50 text-green-700 border border-green-200' :
          'bg-purple-100 text-purple-800'
        }`}>
          {row.record_type === 2 ? 'OPEN PO' : row.record_type === 1 ? 'FORECAST' : RECORD_TYPE_LABELS[row.record_type]}
        </span>
      </td>
      {showFirmDays && (
        <td className="px-3 py-2 text-right whitespace-nowrap font-mono text-xs">
          {row.firm_days != null ? row.firm_days : '-'}
        </td>
      )}
      {showFirmDays && (
        <td className="px-3 py-2 whitespace-nowrap text-xs">{row.plant || '-'}</td>
      )}
      <td
        className={`px-3 py-2 text-right whitespace-nowrap font-mono text-xs ${arrearsClickable ? 'cursor-pointer hover:bg-red-50' : ''}`}
        onClick={arrearsClickable ? () => onCellClick(row, 'arrears', 'Arrears') : undefined}
      >
        {hasArrears ? (
          <span className="text-red-600 font-semibold underline decoration-dotted cursor-pointer">{formatQty(row.arrears)}</span>
        ) : formatQty(row.arrears)}
      </td>
      {weekColumns.map((col) => bucketCell(col.key, col.label, 'bg-blue-50/30', 'hover:bg-blue-100'))}
      {monthColumns.map((col) => bucketCell(col.key, col.label, 'bg-amber-50/30', 'hover:bg-amber-100'))}
      {!weeksOnly && summaryCell('future', 'Future', '')}
      {summaryCell('total', 'Total', 'font-semibold')}
    </tr>
  );
}
